import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import type { PortalUploadedAttachment } from '../../modules/my-tasks/model/myTasks';
import MobileAttachment from './MobileAttachment';

export type MobileActionAttachment = PortalUploadedAttachment & { previewUrl: string; file: File };
type Props = { attachments: readonly MobileActionAttachment[]; disabled: boolean; uploading: boolean;
  onAddFiles: (files: File[]) => Promise<void>; onRemove: (id: string) => void };

function ImageIcon() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="8" cy="9" r="1.5" /><path d="m4 17 5-5 3 3 3-4 5 6" /></svg>;
}

function FileIcon() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 13.5 14.5 8a3 3 0 0 1 4.2 4.2l-7.5 7.5a5 5 0 0 1-7.1-7.1l8-8" /></svg>;
}

function formatSize(size: number) {
  return size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.ceil(size / 1024))} KB`;
}

function MobileComposerAttachments(props: Props) {
  const images = props.attachments.filter(item => item.mimeType.startsWith('image/'));
  const files = props.attachments.filter(item => !item.mimeType.startsWith('image/'));
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // URL and File ownership belongs to the shared action controller, which also uploads.

  useEffect(() => {
    if (!previewUrl) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      setPreviewUrl(null);
    };
    window.addEventListener('keydown', closeOnEscape, true);
    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [previewUrl]);

  const addImages = (event: ChangeEvent<HTMLInputElement>) => {
    void props.onAddFiles(Array.from(event.currentTarget.files ?? []).filter(file => file.type.startsWith('image/')));
    event.currentTarget.value = '';
  };

  const addFiles = (event: ChangeEvent<HTMLInputElement>) => {
    void props.onAddFiles(Array.from(event.currentTarget.files ?? []));
    event.currentTarget.value = '';
  };

  const removeImage = (image: MobileActionAttachment) => {
    if (previewUrl === image.previewUrl) setPreviewUrl(null);
    props.onRemove(image.fileAssetPublicId);
  };

  const removeFile = props.onRemove;

  return <>
    <div className="mobile-action-page__attachment-tools" aria-label="添加图片或附件">
      <button type="button" aria-label="添加图片" title="添加图片" disabled={props.disabled || props.uploading} onClick={() => imageInput.current?.click()}><ImageIcon /></button>
      <button type="button" aria-label="添加附件" title="添加附件" disabled={props.disabled || props.uploading} onClick={() => fileInput.current?.click()}><FileIcon /></button>
      <input ref={imageInput} type="file" accept="image/*" multiple hidden disabled={props.disabled || props.uploading} onChange={addImages} />
      <input ref={fileInput} type="file" multiple hidden disabled={props.disabled || props.uploading} onChange={addFiles} />
    </div>
    {props.uploading ? <p role="status">正在上传附件</p> : null}
    {images.length ? <div className="mobile-action-page__image-grid">{images.map((image) => <div className="mobile-action-page__image" key={image.fileAssetPublicId}>
      <button className="mobile-action-page__image-preview" type="button" aria-label={`预览${image.fileName}`} onClick={() => setPreviewUrl(image.previewUrl)}><img src={image.previewUrl} alt={image.fileName} /></button>
      <button className="mobile-action-page__remove" type="button" disabled={props.disabled || props.uploading} aria-label={`移除${image.fileName}`} onClick={() => removeImage(image)}>×</button>
    </div>)}</div> : null}
    {files.length ? <div className="mobile-action-page__file-grid mobile-attachment-grid">{files.map((file) => <div className="mobile-action-page__file" key={file.fileAssetPublicId}>
      <MobileAttachment name={file.fileName} size={formatSize(file.sizeBytes)} />
      <button className="mobile-action-page__remove" type="button" disabled={props.disabled || props.uploading} aria-label={`移除${file.fileName}`} onClick={() => removeFile(file.fileAssetPublicId)}>×</button>
    </div>)}</div> : null}
    {previewUrl ? createPortal(<div className="mobile-action-image-preview" role="dialog" aria-modal="true" aria-label="图片预览" onClick={() => setPreviewUrl(null)}><img src={previewUrl} alt="所选图片预览" /></div>, document.body) : null}
  </>;
}

export default MobileComposerAttachments;
